import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import { Controls, ControlsButton } from '@/components/Controls';
import InfiniteLoader from '@/components/InfiniteLoader';
import { SrOnly } from '@/components/Layout/Parts';
import { Modal } from '@/components/Modal';
import { Table, TableContent, Td, Th } from '@/components/Table';
import Text from '@/components/Text';
import useNetwork from '@/hooks/dq/useNetwork';
import useAlerts from '@/hooks/useAlerts';
import {
  DQAction,
  DQScheduledActionsPage,
  PaginatedResponse,
} from '@/types/resources';
import { api } from '@/utils/api';
import { DQActionType } from '@/utils/enums';
import { scrollBarStyles } from '@/utils/theme';

const renderActiveWeekdays = (weekdays: string[]) => {
  return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    .filter((d) => weekdays.includes(d))
    .join(', ');
};

const renderActionDescription = (action: DQAction) => {
  if (action.type === DQActionType.CHANGE_FREQUENCY) {
    return `Change frequency<span> to ${action.payload!.frequency} min</span>`;
  }

  if (action.type === DQActionType.STOP_QUEUE) {
    return `Disable queue`;
  }

  if (action.type === DQActionType.START_QUEUE) {
    let description = 'Enable queue';

    if (action.payload?.start_time) {
      description += `<br/>Set start time <span>to ${action.payload.start_time.split(' ').join(' · ')}</span>`;
    }

    if (action.payload?.frequency) {
      description += `<br/>Change frequency <span>to ${action.payload?.frequency} min</span>`;
    }

    return description;
  }

  return '-';
};

const Dialog = styled(StyledDialog)`
  width: 1160px;
  text-align: left;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  height: 72px;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e7e7e7;
  box-shadow: 0px 1px 0px 0px #22222214;
`;

const Body = styled.div`
  padding: 24px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  height: 614px;
  overflow-y: auto;
  ${scrollBarStyles};
`;

const Footer = styled.div`
  border-radius: 0 0 4px 4px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscale};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 112px;
  padding: 0 24px;
  box-shadow: 0px -1px 8px 0px #2222220f;
`;

const StyledCloseButton = styled(CloseButton)`
  position: static;
`;

const ActionsCount = styled.span`
  color: ${({ theme }) => theme.colors.gray};
`;

const NetworkLabel = styled.span`
  text-transform: capitalize;
`;

const StyledControls = styled(Controls)`
  display: inline-flex;
`;

const StyledTable = styled(Table)`
  margin-top: 12px;
  th,
  td {
    font-weight: 700;

    &:nth-child(1) {
      width: 122px;
      padding-left: 24px;
    }

    &:nth-child(2) {
      width: 40%;
    }

    &:last-child {
      padding-right: 24px;
      width: 153px;
      text-align: right;
    }
  }
`;

const Weekdays = styled.div`
  text-overflow: ellipsis;
  overflow: hidden;
  text-wrap: nowrap;
`;

const Description = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const DescIcon = styled.svg`
  color: ${({ theme }) => theme.colors.purple};
  flex-shrink: 0;
`;

const DescriptionText = styled.div`
  line-height: 1.6;
  > span {
    font-weight: 400;
  }
`;

const RecurringBadge = styled(Text)`
  flex-shrink: 0;
  width: 52px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f0fb;
  border-radius: 4px;
`;

const Tr = styled.tr<{ $gray: boolean }>`
  ${({ theme, $gray }) =>
    $gray &&
    css`
      > td {
        background: ${theme.colors.grayscaleExtraLight} !important;
        color: ${theme.colors.gray} !important;
      }

      ${DescIcon} {
        color: ${theme.colors.gray};

        rect {
          fill: ${theme.colors.lightGray} !important;
          fill-opacity: 1 !important;
        }
      }

      ${RecurringBadge} {
        background: ${theme.colors.lightGray};
        color: ${theme.colors.gray};
      }
    `}
`;

const StatusBadge = styled(Text).attrs({
  $color: 'gray',
  $type: 'smallHeadline',
  as: 'div',
})`
  height: 32px;
  display: inline-flex;
  align-items: center;
  padding: 0 16px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.lightGray};
`;

interface Props {
  onClose: VoidFunction;
  page: DQScheduledActionsPage;
}

enum Tabs {
  ALL = 1,
  ONE_TIME = 2,
  RECURRING = 3,
}

const LogModal: FC<Props> = ({ onClose, page }) => {
  const [network, networkLabel] = useNetwork();

  const alerts = useAlerts();

  const [activeTab, setActiveTab] = useState(Tabs.ALL);

  const queryClient = useQueryClient();

  const fetchActions = ({ pageParam = '/v1/editorial/queue/dq-actions' }) =>
    api.get<never, PaginatedResponse<DQAction>>(pageParam, {
      params: {
        'filter[network]': network,
        'filter[channelId]': page.id,
        ...(activeTab === Tabs.ALL && {
          'filter[excludeRecurring]': 1,
        }),
        ...(activeTab === Tabs.ONE_TIME && {
          'filter[fromRecurring]': 0,
          'filter[excludeRecurring]': 1,
        }),
        ...(activeTab === Tabs.RECURRING && {
          'filter[recurringOnly]': 1,
        }),
      },
    });

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    [
      'distribution_queue_actions',
      {
        channelId: page.id,
        network,
        activeTab,
      },
    ],
    fetchActions,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
    },
  );

  const actions = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<DQAction[]>((acc, currKey: any) => {
        return [...acc, ...data!.pages[currKey].data];
      }, []),
    [data],
  );

  const onRemoveAction = async (actionId: number) => {
    try {
      const confirmed = await alerts.confirm('Are you sure?');

      if (!confirmed) return;

      await api.delete(`/v1/editorial/queue/dq-actions/${actionId}`);

      await queryClient.invalidateQueries([
        'distribution_queue_actions',
        {
          channelId: page.id,
          network,
        },
      ]);

      queryClient.invalidateQueries([
        'distribution_queue_pages_and_actions',
        {
          network,
        },
      ]);

      alerts.close();
    } catch (_) {
      alerts.error();
    }
  };

  const renderStatusBadge = (action: DQAction) => {
    return (
      <StatusBadge>{action.finishedAt ? 'Completed' : 'Failed'}</StatusBadge>
    );
  };

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <Header>
          <Text $type="button">
            {page.name} / <NetworkLabel>{networkLabel}</NetworkLabel> / Action
            log{' '}
            <ActionsCount>({data?.pages[0]?.meta?.total ?? 0})</ActionsCount>
          </Text>
          <StyledCloseButton onClick={onClose}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 1L1 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13 13L1 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </StyledCloseButton>
        </Header>
        <Body>
          <StyledControls>
            <ControlsButton
              gray
              blackText
              active={activeTab === Tabs.ALL}
              onClick={() => setActiveTab(Tabs.ALL)}
            >
              All
            </ControlsButton>
            <ControlsButton
              gray
              blackText
              active={activeTab === Tabs.ONE_TIME}
              onClick={() => setActiveTab(Tabs.ONE_TIME)}
            >
              One-time
            </ControlsButton>
            <ControlsButton
              gray
              blackText
              active={activeTab === Tabs.RECURRING}
              onClick={() => setActiveTab(Tabs.RECURRING)}
            >
              Recurring
            </ControlsButton>
          </StyledControls>
          <StyledTable $fixed>
            <thead>
              <tr>
                <Th>Created At</Th>
                <Th>Description</Th>
                <Th>Action Date & Time</Th>
                <Th>Owner</Th>
                <Th>
                  <SrOnly>Actions</SrOnly>
                </Th>
              </tr>
            </thead>
            <tbody>
              <TableContent
                colSpan={5}
                isLoading={isLoading}
                isError={isError}
                hasData={!!actions?.length}
              >
                {actions?.map((action) => (
                  <Tr key={action.id} $gray={action.isCompleted}>
                    <Td>
                      {DateTime.fromISO(action.createdAt).toFormat(
                        'dd/LL/yyyy',
                      )}
                      <br />
                      <Text as="span" $color="gray" $type="extraSmall">
                        {DateTime.fromISO(action.createdAt).toFormat('HH:mm')}
                      </Text>
                    </Td>
                    <Td>
                      <Description>
                        <DescIcon
                          width="32"
                          height="32"
                          viewBox="0 0 32 32"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect
                            width="32"
                            height="32"
                            rx="16"
                            fill="#9F9AE6"
                            fillOpacity="0.15"
                          />
                          <path
                            d="M9 14.5L16 18.5L23 14.5L16 10.5L9 14.5Z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M9 17L16 21L23 17"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </DescIcon>
                        <DescriptionText
                          dangerouslySetInnerHTML={{
                            __html: renderActionDescription(action),
                          }}
                        />
                        {(action.fromRecurring || action.isRecurring) && (
                          <RecurringBadge $color="purple" $type="extraSmall">
                            Recurring
                          </RecurringBadge>
                        )}
                      </Description>
                    </Td>
                    <Td>
                      {action.runAt ? (
                        <>
                          {DateTime.fromISO(action.runAt).toFormat(
                            'dd/LL/yyyy',
                          )}
                          <br />
                          <Text as="span" $color="gray" $type="extraSmall">
                            {DateTime.fromISO(action.runAt).toFormat('HH:mm')}
                          </Text>
                        </>
                      ) : (
                        <>
                          <Weekdays>
                            {renderActiveWeekdays(
                              Object.keys(action.weekdays!),
                            )}
                          </Weekdays>
                          <Text as="span" $color="gray" $type="extraSmall">
                            {action.time}
                          </Text>
                        </>
                      )}
                    </Td>
                    <Td>{action.owner ?? '-'}</Td>
                    <Td>
                      {action.isCompleted ? (
                        renderStatusBadge(action)
                      ) : (
                        <Button
                          small
                          color="red"
                          onClick={() => onRemoveAction(action.id)}
                        >
                          Remove action
                        </Button>
                      )}
                    </Td>
                  </Tr>
                ))}
              </TableContent>
            </tbody>
          </StyledTable>
          <InfiniteLoader
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isFetchingNextPage}
          />
        </Body>
        <Footer>
          <Button type="button" color="secondary" onClick={onClose}>
            Close
          </Button>
        </Footer>
      </Dialog>
    </Modal>
  );
};

export default LogModal;
